import { useState, useEffect, useCallback } from 'react';
import apiClient from '../utils/api';

export interface NavigationStats {
  // 新しいカテゴリ
  pending: number;
  unassigned: number;
  active: number;
  waiting: number;
  completed: number;
  temp_completed: number;
  paused: number;
  uncompleted: number;
  // 未読数
  unreadMessages: number;
  
  // 旧カテゴリ（互換性のため）
  inbox: number;
  priority: number;
  starred: number;
  snoozed: number;
  archived: number;
  teams: number;
}

export interface UseNavigationStatsProps {
  autoRefresh?: boolean;
  refreshInterval?: number;
  onError?: (error: string) => void;
}

export const useNavigationStats = ({
  autoRefresh = true,
  refreshInterval = 30000, // 30秒
  onError,
}: UseNavigationStatsProps = {}) => {
  const [stats, setStats] = useState<NavigationStats>({
    // 新しいカテゴリ
    pending: 0,
    unassigned: 0,
    active: 0,
    waiting: 0,
    completed: 0,
    temp_completed: 0,
    paused: 0,
    uncompleted: 0,
    unreadMessages: 0,
    
    // 旧カテゴリ（互換性のため）
    inbox: 0,
    priority: 0,
    starred: 0,
    snoozed: 0,
    archived: 0,
    teams: 0,
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // 統計データを取得
  const fetchStats = useCallback(async () => {
    try {
      setError(null);
      
      // 新しい統計APIを使用
      const response = await apiClient.getNavigationStats();
      
      if (response.success && response.stats) {
        const apiStats = response.stats;
        
        // APIレスポンスを内部形式に変換
        const newStats: NavigationStats = {
          // 新しいカテゴリ
          pending: apiStats.pending || 0,
          unassigned: apiStats.unassigned || 0,
          active: apiStats.active || 0,
          waiting: apiStats.waiting || 0,
          completed: apiStats.completed || 0,
          temp_completed: apiStats.temp_completed || 0,
          paused: apiStats.paused || 0,
          uncompleted: apiStats.uncompleted || 0,
          unreadMessages: apiStats.unreadMessages || 0,
          
          // 旧カテゴリ（互換性のため）
          inbox: apiStats.inbox || 0,
          priority: apiStats.priority || 0,
          starred: apiStats.starred || 0,
          snoozed: apiStats.snoozed || 0,
          archived: apiStats.archived || 0,
          teams: 4, // オペレーター数から算出（後で動的に）
        };

        setStats(newStats);
        setLastUpdated(new Date());
      } else {
        throw new Error('統計データの取得に失敗しました');
      }
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '統計データの取得に失敗しました';
      setError(errorMessage);
      onError?.(errorMessage);
      
      // ダミーデータは設定せず、既存の値を維持
    } finally {
      setLoading(false);
    }
  }, [onError]);

  // 初回読み込み
  useEffect(() => {
    fetchStats();
  }, []);

  // 自動更新
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchStats();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval]); // fetchStatsを依存配列から削除して無限ループを防止

  // 手動更新
  const refresh = useCallback(() => {
    setLoading(true);
    fetchStats();
  }, [fetchStats]);

  // 特定カテゴリーのカウントをインクリメント（リアルタイム更新用）
  const incrementCount = useCallback((category: keyof NavigationStats) => {
    setStats(prev => ({
      ...prev,
      [category]: prev[category] + 1,
    }));
  }, []);

  // 特定カテゴリーのカウントをデクリメント（リアルタイム更新用）
  const decrementCount = useCallback((category: keyof NavigationStats) => {
    setStats(prev => ({
      ...prev,
      [category]: Math.max(0, prev[category] - 1),
    }));
  }, []);

  // 未読数の増減（リアルタイム）
  const incrementUnread = useCallback(() => {
    setStats(prev => ({ ...prev, unreadMessages: (prev.unreadMessages || 0) + 1 }));
  }, []);

  const clearUnread = useCallback(() => {
    setStats(prev => ({ ...prev, unreadMessages: 0 }));
  }, []);

  return {
    stats,
    loading,
    error,
    lastUpdated,
    refresh,
    incrementCount,
    decrementCount,
    incrementUnread,
    clearUnread,
  };
};

export default useNavigationStats;
